<template>
  <div id="despesas-modal-app">
    <modal
      elem-id="modal_crud_despesa"
      size="compactDesktop"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
      >
      
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Loading overlay while options are being fetched -->
        <div v-if="!isFormReady" class="loading-overlay">
          <spinner style="width:40px;height:40px;" />
        </div>

        <div class="modal-content-wrapper">
          <!-- Action Buttons - Render after form but display first -->
          <div class="action-buttons" v-if="formRef">
            <Button
              data-testid="despesas-modal-reativar-button"
              v-if="shouldShowReativar"
              @click="reativarDespesa"
              severity="success"
              :label="t('Reativar despesa')"
              icon="pi pi-check"
            />
            <Button
              data-testid="despesas-modal-cancelar-button"
              v-if="shouldShowCancelar"
              @click="cancelarDespesa"
              severity="danger"
              :label="t('Cancelar despesa')"
              icon="pi pi-ban"
            />
            <Button
              data-testid="despesas-modal-excluir-button"
              v-if="shouldShowExcluir"
              @click="excluirDespesa"
              severity="danger"
              :label="t('Excluir despesa')"
              icon="pi pi-trash"
            />
          </div>

          <despesas-form
            ref="formRef"
            :is-edit-mode="isEditMode"
            @save-success="handleSaveSuccess"
            @action-success="handleActionSuccess">
          </despesas-form>
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useDespesas } from '../../composables/useDespesas';
import { useConfirmAction } from '../../composables/useConfirmAction';
import DespesasForm from './DespesasForm.vue';

interface DespesaFormInstance {
  canSave: boolean;
  loadForEdit: (despesaId: number) => Promise<void>;
  saveDespesa: () => Promise<void>;
  darBaixa: (despesaId: number) => Promise<void>;
  resetForm: () => void;
  initializeForNew: () => Promise<void>;
  getDespesaData: () => {
    despesa_id: number | null;
    despesa_status: string;
    [key: string]: unknown;
  };
}

interface ModalOpenPayload {
  id?: number;
  action?: string;
}

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { cancelarDespesa: cancelarDespesaAPI, reativarDespesa: reativarDespesaAPI, removerDespesa } = useDespesas();

const emit = defineEmits<{
  close: [];
}>();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<DespesaFormInstance | null>(null);
const isFormReady = ref<boolean>(false);
const pendingInit = ref<'new' | number | null>(null);

// Initialize form when formRef becomes available
watch(formRef, async (newRef) => {
  if (!newRef || isFormReady.value) return;

  if (pendingInit.value === 'new') {
    log('DespesasModal', 'watch:formRef', 'Initializing for new');
    await newRef.initializeForNew();
    isFormReady.value = true;
    pendingInit.value = null;
  } else if (typeof pendingInit.value === 'number') {
    log('DespesasModal', 'watch:formRef', 'Loading for edit', pendingInit.value);
    await newRef.loadForEdit(pendingInit.value);
    isFormReady.value = true;
    pendingInit.value = null;
  }
});

// Computed properties
const modalTitle = computed(() => {
  return isEditMode.value ? t('Editar Despesa') : t('Nova Despesa');
});

const shouldShowReativar = computed(() => {
  log('DespesasModal', 'shouldShowReativar', 'Computing', {
    isEditMode: isEditMode.value,
    hasFormRef: !!formRef.value,
    hasGetDespesaData: !!formRef.value?.getDespesaData
  });
  
  if (!isEditMode.value || !formRef.value || typeof formRef.value.getDespesaData !== 'function') {
    return false;
  }
  
  try {
    const data = formRef.value.getDespesaData();
    log('DespesasModal', 'shouldShowReativar', 'Despesa data', data);
    return data?.despesa_status === '2';
  } catch (error) {
    log('DespesasModal', 'shouldShowReativar', 'Error getting data', error);
    return false;
  }
});

const shouldShowCancelar = computed(() => {
  log('DespesasModal', 'shouldShowCancelar', 'Computing', {
    isEditMode: isEditMode.value,
    hasFormRef: !!formRef.value,
    hasGetDespesaData: !!formRef.value?.getDespesaData
  });
  
  if (!isEditMode.value || !formRef.value || typeof formRef.value.getDespesaData !== 'function') {
    return false;
  }
  
  try {
    const data = formRef.value.getDespesaData();
    log('DespesasModal', 'shouldShowCancelar', 'Despesa data', data);
    return data?.despesa_status === '1';
  } catch (error) {
    log('DespesasModal', 'shouldShowCancelar', 'Error getting data', error);
    return false;
  }
});

const shouldShowExcluir = computed(() => {
  log('DespesasModal', 'shouldShowExcluir', 'Computing', {
    isEditMode: isEditMode.value,
    hasFormRef: !!formRef.value,
    hasGetDespesaData: !!formRef.value?.getDespesaData
  });
  
  if (!isEditMode.value || !formRef.value || typeof formRef.value.getDespesaData !== 'function') {
    return false;
  }
  
  try {
    const data = formRef.value.getDespesaData();
    log('DespesasModal', 'shouldShowExcluir', 'Despesa data', data);
    const status = data?.despesa_status;
    return status === '1' || status === '2';
  } catch (error) {
    log('DespesasModal', 'shouldShowExcluir', 'Error getting data', error);
    return false;
  }
});

// Methods
const openModal = async (): Promise<void> => {
  log('DespesasModal', 'openModal', 'Begin');
  isVisible.value = true;
  isEditMode.value = false;
  isFormReady.value = false;

  await nextTick();

  log('DespesasModal', 'openModal', 'After nextTick', {
    hasFormRef: !!formRef.value,
    formRefType: typeof formRef.value
  });

  if (formRef.value) {
    await formRef.value.initializeForNew();
    isFormReady.value = true;
  } else {
    log('DespesasModal', 'openModal', 'Setting pendingInit to new');
    pendingInit.value = 'new';
  }
};

const loadForEdit = async (despesaId: number): Promise<void> => {
  log('DespesasModal', 'loadForEdit', 'Begin', despesaId);
  isVisible.value = true;
  isEditMode.value = true;
  isFormReady.value = false;

  log('DespesasModal', 'loadForEdit', 'Before nextTick', {
    hasFormRef: !!formRef.value,
    formRefType: typeof formRef.value
  });

  // Wait for the form component to be rendered
  await nextTick();

  log('DespesasModal', 'loadForEdit', 'After nextTick', {
    hasFormRef: !!formRef.value,
    formRefType: typeof formRef.value,
    hasLoadForEdit: !!formRef.value?.loadForEdit,
    hasGetDespesaData: !!formRef.value?.getDespesaData
  });

  if (formRef.value) {
    await formRef.value.loadForEdit(despesaId);
    isFormReady.value = true;
    log('DespesasModal', 'loadForEdit', 'After form loadForEdit');
  } else {
    log('DespesasModal', 'loadForEdit', 'Setting pendingInit to despesaId', despesaId);
    pendingInit.value = despesaId;
  }
};

const closeModal = (): void => {
  isVisible.value = false;
  isEditMode.value = false;
  isFormReady.value = false;
  pendingInit.value = null;

  if (formRef.value) {
    formRef.value.resetForm();
  }

  emit('close');
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    await formRef.value.saveDespesa();
  }
};

const handleSaveSuccess = (): void => {
  eventBus.emit('despesa.saved');
  closeModal();
};

const handleActionSuccess = (): void => {
  eventBus.emit('despesa.saved');
  closeModal();
};

const resetForm = (): void => {
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const runDespesaAction = (
  options: { message: string; header: string; icon: string; acceptLabel: string; rejectLabel: string },
  apiCall: (id: number) => Promise<unknown>,
  successMessage: string,
  errorPrefix: string
): void => {
  confirmAction({
    ...options,
    onAccept: async () => {
      try {
        if (!formRef.value || !formRef.value.getDespesaData) {
          console.error('Form ref or getDespesaData method not available');
          return;
        }

        const despesaData = formRef.value.getDespesaData();
        if (!despesaData?.despesa_id) return;

        await apiCall(despesaData.despesa_id);

        eventBus.emit('globalMessage.success', successMessage);
        eventBus.emit('despesa.saved');
        closeModal();

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error(errorPrefix, error);
        eventBus.emit('globalMessage.error', message || errorPrefix);
      }
    }
  });
};

const cancelarDespesa = (): void => {
  runDespesaAction(
    {
      message: t('Tem certeza que deseja cancelar esta despesa?'),
      header: t('Confirmação'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Sim'),
      rejectLabel: t('Não')
    },
    cancelarDespesaAPI,
    t('Despesa cancelada com sucesso!'),
    t('Erro ao cancelar despesa')
  );
};

const reativarDespesa = (): void => {
  runDespesaAction(
    {
      message: t('Tem certeza que deseja reativar esta despesa?'),
      header: t('Confirmação'),
      icon: 'pi pi-question-circle',
      acceptLabel: t('Sim'),
      rejectLabel: t('Não')
    },
    reativarDespesaAPI,
    t('Despesa reativada com sucesso!'),
    t('Erro ao reativar despesa')
  );
};

const excluirDespesa = (): void => {
  runDespesaAction(
    {
      message: t('Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.'),
      header: t('Confirmação'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Sim, excluir'),
      rejectLabel: t('Cancelar')
    },
    removerDespesa,
    t('Despesa excluída com sucesso!'),
    t('Erro ao excluir despesa')
  );
};

// Standard modal open handler (for modal.despesas.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('DespesasModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    loadForEdit(data.id);
  } else {
    openModal();
  }
};

const handleLegacyOpenEdit = (despesaId?: number): void => {
  if (typeof despesaId === 'number') {
    loadForEdit(despesaId);
  }
};

// Event listeners for opening modal via eventBus
onMounted(() => {
  log('DespesasModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.despesas.open', handleModalOpen);
  eventBus.on('modal.despesas.close', closeModal);

  // Legacy events (for backward compatibility)
  eventBus.on('despesa.open-modal-new', openModal);
  eventBus.on('despesa.open-modal-edit', handleLegacyOpenEdit);
});

onBeforeUnmount(() => {
  log('DespesasModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.despesas.open', handleModalOpen);
  eventBus.off('modal.despesas.close', closeModal);

  // Remove legacy events
  eventBus.off('despesa.open-modal-new', openModal);
  eventBus.off('despesa.open-modal-edit', handleLegacyOpenEdit);
});

// Expose public methods for parent components
defineExpose({
  openModal,
  loadForEdit,
  closeModal,
  resetForm
});
</script>

<style scoped>
.modal-content-wrapper {
  display: flex;
  flex-direction: column;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
  order: -1;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 2rem;
  z-index: 10;
  pointer-events: none;
}
</style>
